/**
 * ToolResultsDisplay - Generic Tool Result Visualization Dispatcher
 *
 * This component is the entry point for rendering tool results.
 * It detects tool types from run results and dispatches to appropriate renderers.
 *
 * Architecture:
 * 1. Receives raw data from workflow run results
 * 2. Parses/detects tool type using parseToolResult()
 * 3. Looks up renderer in registry
 * 4. Dispatches to renderer component
 * 5. Falls back to DefaultTextRenderer for unknown tools
 *
 * Usage:
 *   <ToolResultsDisplay data={runResult.final_output} />
 *
 * The component handles:
 * - Type detection and parsing
 * - Error boundaries for renderer failures
 * - Fallback to default renderer
 * - Copy-to-clipboard functionality
 */

import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { parseToolResult, isToolResult } from '../../types/toolResults';
import { getRendererForToolType, hasCustomRenderer } from './renderers';
import type { ToolResult } from '../../types/toolResults';

interface ToolResultsDisplayProps {
  data: any;
  onCopy?: (text: string) => void;
  className?: string;
}

interface ToolResultsDisplayState {
  error: Error | null;
  hasError: boolean;
}

/**
 * Error boundary component for tool renderers.
 * Catches errors in renderer components and displays fallback UI.
 */
class ToolRendererErrorBoundary extends React.Component<
  { children: React.ReactNode; toolType: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; toolType: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error rendering tool result (${this.props.toolType}):`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Renderer Error
              </h3>
              <p className="text-sm text-red-800 mb-3">
                Failed to render tool result for tool type: <code className="font-mono bg-red-100 px-1 py-0.5 rounded">{this.props.toolType}</code>
              </p>
              <details className="text-sm">
                <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                  Error Details
                </summary>
                <pre className="mt-2 p-3 bg-red-100 rounded text-xs text-red-900 overflow-x-auto">
                  {this.state.error?.message || 'Unknown error'}
                  {'\n\n'}
                  {this.state.error?.stack || ''}
                </pre>
              </details>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Main ToolResultsDisplay component.
 *
 * Detects tool results and dispatches to appropriate renderers.
 */
const ToolResultsDisplay: React.FC<ToolResultsDisplayProps> = ({
  data,
  onCopy,
  className = ''
}) => {
  const [showFormatted, setShowFormatted] = useState(true);

  // Try to parse data as a tool result
  const toolResult = parseToolResult(data);

  // If not a tool result, return null (component will not render)
  if (!toolResult) {
    return null;
  }

  // Validate tool result structure
  if (!isToolResult(toolResult)) {
    console.warn('Invalid tool result structure:', toolResult);
    return null;
  }

  // Get the appropriate renderer for this tool type
  const Renderer = getRendererForToolType(toolResult.toolType);
  const hasCustom = hasCustomRenderer(toolResult.toolType);

  // Log renderer selection (helpful for debugging)
  if (import.meta.env.DEV) {
    console.log(
      `[ToolResultsDisplay] Rendering tool result:`,
      {
        toolType: toolResult.toolType,
        toolName: toolResult.toolName,
        hasCustomRenderer: hasCustom,
        rendererComponent: Renderer.name
      }
    );
  }

  // Pretty-printed JSON for the Raw view. Tries to parse string payloads so the
  // user sees structured JSON, but falls back to the raw string if it isn't JSON.
  const rawJson = (() => {
    if (typeof data === 'string') {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return data;
      }
    }
    return JSON.stringify(data, null, 2);
  })();

  const handleCopyRaw = () => {
    if (onCopy) {
      onCopy(rawJson);
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(rawJson).catch(() => undefined);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-end space-x-2 mb-2">
        <button
          onClick={() => setShowFormatted((prev) => !prev)}
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            showFormatted
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-blue-700'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
          aria-label={showFormatted ? 'Show raw JSON' : 'Show formatted view'}
        >
          {showFormatted ? 'Raw JSON' : 'Formatted'}
        </button>
        {!showFormatted && (
          <button
            onClick={handleCopyRaw}
            className="px-3 py-1 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-md transition-colors border border-green-300 dark:border-green-700"
          >
            Copy
          </button>
        )}
      </div>
      {showFormatted ? (
        <ToolRendererErrorBoundary toolType={toolResult.toolType}>
          <Renderer
            result={toolResult}
            onCopy={onCopy}
          />
        </ToolRendererErrorBoundary>
      ) : (
        <pre className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-xs sm:text-sm text-gray-800 dark:text-gray-200 overflow-auto max-h-[60vh] whitespace-pre-wrap break-words font-mono">
          {rawJson}
        </pre>
      )}
    </div>
  );
};

export default ToolResultsDisplay;
